/**
 * ForwardListPage — Forward P / LocalPver2 với Top-up Alert.
 * UX/UI đồng bộ ContractListView. Highlight row khi top_up_alert <> ''.
 */
import { type MouseEvent, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { useNavigate } from 'react-router-dom'

import { usePagePermission } from '@/hooks/usePagePermission'
import { useListLocalPVer2Query } from '@/store/api/localPVer2Api'
import type { LocalPVer2Item } from '@/types/api'
import { sumNumericColumn } from '@/utils/tableTotals'

import DepositFormDialog from './DepositFormDialog'

type FilterKind = 'search' | 'contractType' | 'alertOnly'

const ALL_ROWS_PAGE_SIZE = 1_000_000

const filterBarBtnSx: SxProps<Theme> = (theme) => ({
  minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, textTransform: 'none', fontSize: '0.7rem', lineHeight: 1.1,
  '&.MuiButton-outlined': {
    borderColor: alpha(theme.palette.text.primary, 0.14),
    '&:hover': { borderColor: alpha(theme.palette.text.primary, 0.24), backgroundColor: alpha(theme.palette.text.primary, 0.04) },
  },
})
const actionToolbarBtnSx: SxProps<Theme> = { minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, fontSize: '0.72rem', lineHeight: 1.1, textTransform: 'none' }

type ColumnKind = 'text' | 'date' | 'number' | 'percent' | 'vnd'
type ColumnDef = {
  key: keyof LocalPVer2Item | '_supplier' | '_alert_marker'
  label: string
  kind?: ColumnKind
  align?: 'right'
}
const NON_SUM_TOTAL_KEYS = new Set<string>([])

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'top_up_alert', label: 'Top-up Alert' },
  { key: 'contract_no', label: 'Số HĐ' },
  { key: '_supplier', label: 'Nhà cung cấp' },
  { key: 'contract_date', label: 'Ngày HĐ', kind: 'date' },
  { key: 'contract_type', label: 'Loại' },
  { key: 'quantity_kg', label: 'SL (kg)', kind: 'number', align: 'right' },
  { key: 'received', label: 'Đã nhận', kind: 'number', align: 'right' },
  { key: 'to_be_received', label: 'Còn nhận', kind: 'number', align: 'right' },
  { key: 'deposit_price', label: 'Deposit Price', kind: 'vnd', align: 'right' },
  { key: 'deposit_value_per', label: 'Deposit Value (%)', kind: 'percent', align: 'right' },
  { key: 'deposit_limit_per', label: 'Deposit Limit (%)', kind: 'percent', align: 'right' },
  { key: 'deposit_time_limit', label: 'Time Limit (d)', kind: 'number', align: 'right' },
  { key: 'deposit_bonus_per', label: 'Top Up (%)', kind: 'percent', align: 'right' },
  { key: 'deposit_interest_per', label: 'Interest (%/m)', kind: 'percent', align: 'right' },
  { key: 'deposit_repayment_date', label: 'Repayment Date', kind: 'date' },
  { key: 'top_up_value', label: 'Top-up Value', kind: 'vnd', align: 'right' },
  { key: 'total_deposit', label: 'Total Deposit', kind: 'vnd', align: 'right' },
  { key: 'total_interest', label: 'Total Interest', kind: 'vnd', align: 'right' },
  { key: 'total_repayment', label: 'Total Repayment', kind: 'vnd', align: 'right' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatAccessDate = (v: unknown): string => { if (!v) return ''; const d = new Date(String(v)); if (Number.isNaN(d.getTime())) return String(v); return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${`${d.getFullYear()}`}` }
const formatAccessNumber = (v: number): string => Number.isInteger(v) ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const formatVnd = (v: number | null | undefined): string => v == null ? '' : `${formatAccessNumber(v)} ₫`

const distinctValues = (rows: LocalPVer2Item[], key: keyof LocalPVer2Item): string[] => {
  const values = new Set<string>()
  for (const r of rows) { const v = r[key]; if (v == null) continue; const t = String(v).trim(); if (t) values.add(t) }
  return [...values].sort((a, b) => a.localeCompare(b))
}

const rowMatchesSearch = (row: LocalPVer2Item, term: string): boolean => {
  const needle = term.trim().toLowerCase()
  if (!needle) return true
  const fields: Array<keyof LocalPVer2Item> = ['contract_no', 'supplier_code', 'supplier_name', 'contract_type', 'top_up_alert']
  return fields.some((k) => { const v = row[k]; return v != null && String(v).toLowerCase().includes(needle) })
}

const cellValue = (row: LocalPVer2Item, col: ColumnDef): string => {
  if (col.key === '_supplier') return `${row.supplier_code ?? ''} — ${row.supplier_name ?? ''}`
  if (col.key === '_alert_marker') return row.top_up_alert ? '!' : ''
  const val = row[col.key as keyof LocalPVer2Item]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'percent' && typeof val === 'number') return formatAccessNumber(val * 100)
  if (col.kind === 'vnd' && typeof val === 'number') return formatVnd(val)
  if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  if (typeof val === 'number') return formatAccessNumber(val)
  return String(val)
}

export function ForwardListPage() {
  const { canEdit } = usePagePermission()
  const navigate = useNavigate()

  const [selectedContractNo, setSelectedContractNo] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  const [searchTerm, setSearchTerm] = useState('')
  const [contractTypeFilter, setContractTypeFilter] = useState('')
  const [alertOnly, setAlertOnly] = useState(false)

  const [filterPopover, setFilterPopover] = useState<{ kind: FilterKind; anchor: HTMLElement } | null>(null)
  const closeFilterPopover = () => setFilterPopover(null)
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => setFilterPopover({ kind, anchor: e.currentTarget })

  const hasActiveFilters = Boolean(searchTerm.trim() || contractTypeFilter || alertOnly)
  const clearAllFilters = () => { setSearchTerm(''); setContractTypeFilter(''); setAlertOnly(false); setPage(0) }

  const [depositOpen, setDepositOpen] = useState(false)

  const { data, isLoading, isFetching, error } = useListLocalPVer2Query({ page: 1, page_size: ALL_ROWS_PAGE_SIZE })

  const baseRows = useMemo(() => data?.items ?? [], [data])

  const filteredRows = useMemo(() =>
    baseRows.filter((row) => {
      if (!rowMatchesSearch(row, searchTerm)) return false
      if (contractTypeFilter && row.contract_type !== contractTypeFilter) return false
      if (alertOnly && !(row.top_up_alert && row.top_up_alert !== '')) return false
      return true
    }),
    [baseRows, searchTerm, contractTypeFilter, alertOnly],
  )

  const rows = useMemo(() => rowsPerPage === -1 ? filteredRows : filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [filteredRows, page, rowsPerPage])

  const contractTypeOptions = useMemo(() => distinctValues(baseRows, 'contract_type'), [baseRows])

  const totalRowValues = useMemo(
    () =>
      new Map(
        TABLE_COLUMNS.filter(
          (col) => col.kind === 'number' && typeof col.key === 'string' && !col.key.startsWith('_') && !NON_SUM_TOTAL_KEYS.has(String(col.key)),
        ).map((col) => [String(col.key), sumNumericColumn(filteredRows, col.key as keyof LocalPVer2Item)]),
      ),
    [filteredRows],
  )

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`
  const selectedRow = useMemo(() => baseRows.find((r) => r.contract_no === selectedContractNo) ?? null, [baseRows, selectedContractNo])

  const handleView = () => { if (!selectedRow) return; navigate(`/purchase/local-p/${encodeURIComponent(selectedRow.contract_no)}`) }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" flexWrap="wrap" useFlexGap justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>Filter</Typography>
          <Button size="small" variant={searchTerm.trim() ? 'contained' : 'outlined'} color={searchTerm.trim() ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('search', e)} sx={filterBarBtnSx}>Search</Button>
          <Button size="small" variant={contractTypeFilter ? 'contained' : 'outlined'} color={contractTypeFilter ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('contractType', e)} sx={filterBarBtnSx}>Contract Type</Button>
          <Button size="small" variant={alertOnly ? 'contained' : 'outlined'} color={alertOnly ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('alertOnly', e)} sx={filterBarBtnSx}>Alerts</Button>
          {hasActiveFilters && <Button size="small" variant="text" color="secondary" onClick={clearAllFilters} sx={filterBarBtnSx}>Clear All</Button>}
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => setDepositOpen(true)}>Thêm Deposit</Button>
          <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={!selectedRow} onClick={handleView}>View HĐ</Button>
        </Stack>
      </Stack>
      <Divider sx={{ mb: 0.5 }} />

      {error ? <Alert severity="error" sx={{ mb: 1 }}>Failed to load Forward list.</Alert> : null}

      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', minHeight: 30, py: 0.125 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.68rem' }}>Total: {filteredRows.length}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.68rem' }}>Loaded: {baseRows.length}</Typography>
            <TablePagination component="div" count={filteredRows.length} page={rowsPerPage === -1 ? 0 : page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]} labelRowsPerPage="Rows" sx={{ flexShrink: 0, '& .MuiTablePagination-toolbar': { minHeight: 32, px: 0.5 }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': { fontSize: '0.72rem' } }} />
          </Stack>
        </Box>

        <Popover open={filterPopover !== null} anchorEl={filterPopover?.anchor ?? null} onClose={closeFilterPopover} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }} PaperProps={{ sx: { p: 1.5, minWidth: 240, maxWidth: 360 } }}>
          {filterPopover?.kind === 'search' && (
            <Stack spacing={1}>
              <TextField size="small" fullWidth autoFocus label="Tìm kiếm" placeholder="Số HĐ / NCC..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0) }} />
              <Button size="small" variant="text" onClick={() => { setSearchTerm(''); setPage(0); closeFilterPopover() }}>Clear Search</Button>
            </Stack>
          )}
          {filterPopover?.kind === 'contractType' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Contract Type</InputLabel>
              <Select label="Contract Type" value={contractTypeFilter} onChange={(e) => { setContractTypeFilter(String(e.target.value)); setPage(0); closeFilterPopover() }}>
                <MenuItem value="">All / Clear</MenuItem>
                {contractTypeOptions.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {filterPopover?.kind === 'alertOnly' && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Switch checked={alertOnly} onChange={(e) => { setAlertOnly(e.target.checked); setPage(0) }} />
              <Typography variant="body2">Chỉ hiện hợp đồng có cảnh báo</Typography>
            </Stack>
          )}
        </Popover>

        <Box sx={{
          flex: 1, minHeight: 320, overflow: 'hidden',
          '& .topup-alert-row': { bgcolor: (t) => alpha(t.palette.error.main, 0.08), '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, 0.16) } },
          '& .topup-alert-cell': { bgcolor: (t) => t.palette.error.light, color: (t) => t.palette.error.contrastText, fontWeight: 700 },
        }}>
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col) => (
                  <TableCell key={String(col.key)} align={col.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default' }}>{col.label}</TableCell>
                ))}
              </TableRow>
            )}
            fixedFooterContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col, index) => {
                  const total = totalRowValues.get(String(col.key))
                  const val = total == null ? '' : formatAccessNumber(total)
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      sx={{
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        py: 0.5,
                        bgcolor: 'background.paper',
                        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {index === 0 ? 'Total' : val}
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_: number, row: LocalPVer2Item) =>
              TABLE_COLUMNS.map((col) => {
                const val = cellValue(row, col)
                const isAlertCell = col.key === 'top_up_alert' && row.top_up_alert && row.top_up_alert !== ''
                return (
                  <TableCell key={String(col.key)} align={col.align} className={isAlertCell ? 'topup-alert-cell' : ''} sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                    <Box component="span" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>{val || '-'}</Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 2700, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const row = Number.isFinite(idx) ? rows[idx] : null
                const cls = row?.top_up_alert ? 'topup-alert-row' : ''
                return <TableRow {...props} className={cls} hover selected={row?.contract_no != null && row.contract_no === selectedContractNo} sx={{ cursor: 'pointer' }} onClick={() => { if (row?.contract_no) setSelectedContractNo(row.contract_no) }} />
              },
            }}
          />
        </Box>
      </Box>

      <DepositFormDialog open={depositOpen} mode="create" onClose={() => setDepositOpen(false)} />
    </Box>
  )
}

export default ForwardListPage
